import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  applyBrandingToCss,
  cacheBranding,
  fetchBrandingSettings,
  getDefaultBranding,
  loadBrandingFromCache,
  toBranding,
} from "../services/branding";

const BrandingContext = createContext({
  branding: getDefaultBranding(),
  loading: true,
  error: null,
  refresh: () => {},
});

export const BrandingProvider = ({ children, slug = "default" }) => {
  const [branding, setBranding] = useState(() => getDefaultBranding());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const syncBranding = useCallback((record) => {
    const normalized = toBranding(record);
    setBranding(normalized);
    applyBrandingToCss(normalized.raw);
  }, []);

  const loadBranding = useCallback(
    async (options = { skipCache: false }) => {
      setLoading(true);
      setError(null);
      const shouldSkipCache = options?.skipCache;

      if (!shouldSkipCache) {
        const cached = loadBrandingFromCache();
        if (cached) {
          syncBranding(cached);
          setLoading(false);
          return;
        }
      }

      try {
        const data = await fetchBrandingSettings(slug);
        syncBranding(data);
        cacheBranding(data);
      } catch (err) {
        console.error("Failed to load branding settings:", err);
        setError(err);
        if (!shouldSkipCache) {
          const cached = loadBrandingFromCache();
          if (cached) {
            syncBranding(cached);
            setLoading(false);
            return;
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [slug, syncBranding]
  );

  useEffect(() => {
    applyBrandingToCss(branding.raw);
  }, [branding]);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  const value = useMemo(
    () => ({
      branding,
      loading,
      error,
      refresh: () => loadBranding({ skipCache: true }),
    }),
    [branding, loading, error, loadBranding]
  );

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    throw new Error("useBranding must be used inside BrandingProvider");
  }
  return ctx;
};
